import { hashResponse } from '../../connectivity/degraded/adaptive-poll-state.js';
import type { ConnectivityState } from '../../connectivity/fsm/state-machine.js';

export const DEFAULT_POLL_INTERVAL_MS = 15_000;
export const OUTAGE_POLL_INTERVALS_MS = [4_000, 8_000, 16_000, 32_000] as const;

export interface PollingPolicy {
  readonly hasNats: boolean;
  readonly intervalMs?: number | false;
  readonly explicit?: boolean;
}

export function resolvePollingInterval(
  policy: PollingPolicy,
  state: ConnectivityState,
  recovering: boolean,
  adaptiveMs: number,
): number | false {
  if (state === 'OFFLINE' || state === 'DEGRADED_NO_REST') return false;
  if (policy.intervalMs === false) return false;
  if (policy.explicit === true) return policy.intervalMs ?? false;
  const regular = policy.intervalMs ?? (policy.hasNats ? false : DEFAULT_POLL_INTERVAL_MS);
  if (policy.hasNats && (state === 'DEGRADED_NO_NATS' || recovering)) {
    return regular === false ? adaptiveMs : Math.min(regular, adaptiveMs);
  }
  return regular;
}

export function pollingDelay(intervalMs: number, explicit: boolean, random: () => number): number {
  return explicit
    ? intervalMs
    : Math.round(intervalMs * (0.9 + Math.max(0, Math.min(1, random())) * 0.1));
}

/** Only transport-envelope fields are ignored; domain timestamps remain meaningful. */
export function pollingResponseHash(response: unknown): string {
  if (response === null || typeof response !== 'object' || Array.isArray(response)) {
    return hashResponse(response);
  }
  const {
    requestId: _requestId,
    lastSeq: _lastSeq,
    snapshotSequence: _sequence,
    ...data
  } = response as Record<string, unknown>;
  return hashResponse(data);
}
